"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";

interface Alert {
  id: string;
  type: string;
  title: string;
  body: string | null;
  eventId: string | null;
  severity: string;
  createdAt: string;
  read?: boolean;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  async function load() {
    try {
      const r = await fetch("/api/alerts", { cache: "no-store" });
      if (!r.ok) return;
      const d = await r.json();
      setAlerts(Array.isArray(d.alerts) ? d.alerts : []);
      setUnread(typeof d.unreadCount === "number" ? d.unreadCount : 0);
    } catch {
      /* ignore transient errors */
    }
  }

  // Poll every 30s while mounted.
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  // Close the dropdown on outside click.
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  async function markRead(id: string) {
    setAlerts((a) => a.map((x) => (x.id === id ? { ...x, read: true } : x)));
    setUnread((u) => Math.max(0, u - 1));
    try {
      await fetch("/api/alerts/read", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
    } catch {
      /* optimistic — ignore */
    }
  }

  async function markAll() {
    setAlerts((a) => a.map((x) => ({ ...x, read: true })));
    setUnread(0);
    try {
      await fetch("/api/alerts/read", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ all: true }),
      });
    } catch {
      /* optimistic — ignore */
    }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-lg hover:bg-[hsl(243,40%,97%)] transition-colors text-[hsl(243,15%,45%)] hover:text-[hsl(243,30%,12%)]"
        aria-label="Notifications"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
        </svg>
        {unread > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[hsl(0,72%,51%)] text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-[440px] overflow-auto bg-white border border-[hsl(243,20%,88%)] rounded-xl shadow-lg z-50">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-[hsl(243,20%,92%)] sticky top-0 bg-white">
            <span className="text-sm font-semibold text-[hsl(243,30%,15%)]">Notifications</span>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs text-[hsl(243,40%,52%)] hover:underline">
                Mark all read
              </button>
            )}
          </div>

          {alerts.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-[hsl(243,15%,55%)]">
              No notifications yet.
            </div>
          ) : (
            <ul className="divide-y divide-[hsl(243,20%,94%)]">
              {alerts.map((a) => {
                const inner = (
                  <div className="flex gap-2.5 px-4 py-3 hover:bg-[hsl(243,40%,98%)] cursor-pointer">
                    <span
                      className={
                        "mt-1.5 w-2 h-2 rounded-full shrink-0 " +
                        (a.read
                          ? "bg-transparent border border-[hsl(243,20%,80%)]"
                          : a.severity === "warning"
                            ? "bg-[hsl(28,90%,55%)]"
                            : "bg-[hsl(243,40%,52%)]")
                      }
                    />
                    <div className="min-w-0">
                      <p
                        className={
                          "text-sm leading-snug " +
                          (a.read
                            ? "text-[hsl(243,15%,45%)]"
                            : "text-[hsl(243,30%,15%)] font-semibold")
                        }
                      >
                        {a.title}
                      </p>
                      {a.body && (
                        <p className="text-xs text-[hsl(243,15%,52%)] mt-0.5">{a.body}</p>
                      )}
                      <p className="text-[10px] text-[hsl(243,15%,60%)] mt-1">
                        {new Date(a.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                );
                return (
                  <li key={a.id} onClick={() => markRead(a.id)}>
                    {a.eventId ? (
                      <Link href={`/events/${a.eventId}`} onClick={() => setOpen(false)}>
                        {inner}
                      </Link>
                    ) : (
                      inner
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          <div className="border-t border-[hsl(243,20%,92%)] px-4 py-2 text-center sticky bottom-0 bg-white">
            <Link
              href="/manage/alerts"
              onClick={() => setOpen(false)}
              className="text-xs text-[hsl(243,40%,52%)] hover:underline"
            >
              View all notifications
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
